'use client'

import * as React from 'react'
import { DataTable } from '@/components/data-table/data-table'
import { useDataTable } from '@/hooks/use-data-table'
import { DataTableSortList } from '@/components/data-table/data-table-sort-list'

import { useShippingRatesTableColumns } from './shipping-rates-table-columns'
import { ShippingRatesTableActions } from './shipping-rates-table-actions'
import { ShippingRatesTableActionBar } from './shipping-rates-table-action-bar'
import { ShippingRatesTableSkeleton } from './ShippingRatesTableSkeleton'
import { MainTableState } from '@/lib/utils/table-state-utils'
import type { TableConfig } from '@/types/data-table'
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table'
import { ShippingRateItem } from '@/lib/api/shippingRates'
import { DeleteShippingRateModal } from './delete-shipping-rate-modal'
import { useStoreNavigate } from '@/hooks/use-app-navigate'

interface ShippingRatesTableProps {
	shippingRates: ShippingRateItem[]
	total: number
	loading: boolean
	onTableStateChange: (state: MainTableState) => void
	onRefetch: () => void
	config?: TableConfig
}

/**
 * ShippingRatesTable component for displaying shipping rates with DataTable functionality
 */
export function ShippingRatesTable({
	shippingRates,
	total,
	loading,
	onTableStateChange,
	onRefetch,
	config = {},
}: ShippingRatesTableProps) {
	const { storeRoutes, navigate, id: storeId } = useStoreNavigate()
	const [shippingRateId, setShippingRateId] = React.useState<string | undefined>(undefined)

	const columns = useShippingRatesTableColumns({
		storeId: storeId,
		onEditShippingRate: (id: string) => {
			// Navigate to shipping rate details page if route exists
			const shippingDetailsRoute = storeRoutes.SHIPPING_EDIT?.(id)
			if (shippingDetailsRoute) {
				navigate(shippingDetailsRoute)
			}
		},
		onDeleteShippingRate: (id: string) => setShippingRateId(id),
	})

	const { table } = useDataTable({
		data: shippingRates,
		columns,
		total,
		config,
		onTableStateChange,
		getRowId: (row: ShippingRateItem) => row.id,
	})

	return (
		<DataTable
			table={table}
			actionBar={<ShippingRatesTableActionBar table={table} />}
			isLoading={loading}
			skeletonRows={<ShippingRatesTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
		>
			<ShippingRatesTableActions table={table} onRefresh={onRefetch} isLoading={loading} storeId={storeId} />
			<DataTableSortList table={table} align="end" />
			<DeleteShippingRateModal shippingRateId={shippingRateId} onClose={() => setShippingRateId(undefined)} />
		</DataTable>
	)
}

